import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import configServ from "../../services/config";
import QualificationList from "./QualificationList";
import toast from "react-hot-toast";
import WorkExperienceList from "./WorkExperienceList";

const WorkExperienceForm = ({ employeeId, companyId }) => {
  // 1. STATE INITIALIZATION BASED ON SQL SCHEMA
  const [workExperiences, setWorkExperiences] = useState([]);
  const [formData, setFormData] = useState({
    company_name: "",
    designation: "",
    joining_date: "",
    ending_date: "",
    currently_working: 0, // TinyInt standard (0 = No, 1 = Yes)
    experience: "",
    company_url: "",
    employee_type: "full_time", // Default ENUM fallback
  });

  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // 2. FORM INPUT CHANGES CONTROLLER
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // 3. CHECKBOX HANDLER (CURRENTLY WORKING)
  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      currently_working: isChecked ? 1 : 0,
      // Smart feature: current company me working hone par ending_date automatically empty out ho jayegi
      ending_date: isChecked ? "" : prev.ending_date,
    }));
    if (isChecked && errors.ending_date) {
      setErrors((prev) => ({ ...prev, ending_date: "" }));
    }
  };

  // 4. CLIENT-SIDE FORM VALIDATIONS
  const validateForm = () => {
    let tempErrors = {};
    if (!formData.company_name.trim()) tempErrors.company_name = "Company Name is required";
    if (!formData.designation.trim()) tempErrors.designation = "Designation is required";
    if (!formData.joining_date) tempErrors.joining_date = "Joining Date is required";

    // Validating Ending date only when not currently working there
    if (formData.currently_working === 0) {
      if (!formData.ending_date) {
        tempErrors.ending_date = "Ending Date is required";
      } else if (new Date(formData.joining_date) > new Date(formData.ending_date)) {
        tempErrors.ending_date = "Ending Date cannot be before Joining Date";
      }
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // 5. POST EXECUTION METHOD FOR SENDING EXPERIENCE DETAILS
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitLoading(true);

      // Backend requirements filtering
      const payload = {
        ...formData,
        employee_id: employeeId || "",
        company_id: companyId || "",

        ending_date: formData.currently_working === 1 ? null : formData.ending_date,
      };

      const response = await configServ.saveWorkExperience(payload);

      if (response.status) {
        toast.success("Work experience saved successfully!");
        getWorkExperiences();

        // Resetting form data after successful entry

        setFormData({
          company_name: "",
          designation: "",
          joining_date: "",
          ending_date: "",
          currently_working: 0,
          experience: "",
          company_url: "",
          employee_type: "full_time",
        });

      } else {
        toast.error(response.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Backend transmission failed:", error);
      toast.error("Server submission processing error.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const getWorkExperiences = async () => {
    try {
      const response = await configServ.getWorkExperiences({ employee_id: employeeId, company_id: companyId });
      if (response.status) {
        setWorkExperiences(response.data);
      } else {
        setWorkExperiences([]);
        toast.error(response.message || "Failed to fetch work experiences.");
      }
    } catch (error) {
      console.error("Failed to fetch work experiences:", error);
      toast.error("Failed to fetch work experiences.");
    }
  };

  useEffect(() => {
    if (employeeId) {
      getWorkExperiences();
    }
  }, [employeeId]);

  const handleDeleteExperience = async (id) => {
    try {
      const response = await configServ.deleteWorkExperience(id);
      if (response.status) {
        toast.success("Experience record deleted successfully!");
        getWorkExperiences();
      } else {
        toast.error(response.message || "Failed to delete experience record.");
      }
    } catch (error) {
      console.error("Failed to delete experience record:", error);
      toast.error("Failed to delete experience record.");
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      <Grid container spacing={2.5}>
        {/* Company Identity Entry */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            label="Company Name"
            placeholder="e.g. Google India"
            error={!!errors.company_name}
            helperText={errors.company_name}
            variant="outlined"
          />
        </Grid>

        {/* Professional Designation Text */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="designation"
            value={formData.designation}
            onChange={handleChange}
            label="Designation"
            placeholder="e.g. Senior Software Engineer"
            error={!!errors.designation}
            helperText={errors.designation}
            variant="outlined"
          />
        </Grid>

        {/* Employment Category Types Dropdown */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            name="employee_type"
            label="Employment Type"
            value={formData.employee_type}
            onChange={handleChange}
            variant="outlined"
          >
            <MenuItem value="full_time">Full-Time</MenuItem>
            <MenuItem value="part_time">Part-Time</MenuItem>
            <MenuItem value="contract">Contract Base</MenuItem>
            <MenuItem value="internship">Internship</MenuItem>
          </TextField>
        </Grid>

        {/* Company Official Domain Reference URL */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            name="company_url"
            value={formData.company_url}
            onChange={handleChange}
            label="Company Website URL"
            placeholder="https://example.com"
            variant="outlined"
          />
        </Grid>

        {/* Total Working Span Text Description */}
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            name="experience"
            value={formData.experience}
            onChange={handleChange}
            label="Experience Summary"
            placeholder="e.g. 2 Years 4 Months"
            variant="outlined"
          />
        </Grid>

        {/* Timeline Tracking - Joining Date Field */}
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="date"
            name="joining_date"
            value={formData.joining_date}
            onChange={handleChange}
            label="Joining Date"
            InputLabelProps={{ shrink: true }} // Floating label behavior configuration
            error={!!errors.joining_date}
            helperText={errors.joining_date}
            variant="outlined"
          />
        </Grid>

        {/* Timeline Tracking - Conditional Ending Date Field */}
        {formData.currently_working === 0 && (
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="date"
              name="ending_date"
              value={formData.ending_date}
              onChange={handleChange}
              label="Ending Date"
              InputLabelProps={{ shrink: true }}
              error={!!errors.ending_date}
              helperText={errors.ending_date}
              variant="outlined"
            />
          </Grid>
        )}

        {/* Currently Working Toggle Section */}
        <Grid item xs={12} sx={{ mt: -0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.currently_working === 1}
                onChange={handleCheckboxChange}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight="600" color="#475569">
                I am currently working in this role / company
              </Typography>
            }
          />
        </Grid>
      </Grid>

      {/* FORM ACTIONS PANEL CONTROL */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={submitLoading}
          startIcon={submitLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          sx={{
            borderRadius: "10px",
            px: 4,
            py: 1.2,
            fontWeight: "600",
            textTransform: "none",
            background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
            boxShadow: "0 4px 12px rgba(37, 99, 235, 0.2)",
            "&:hover": {
              background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
            },
          }}
        >
          {submitLoading ? "Processing..." : "Save Experience"}
        </Button>
      </Box>
      <WorkExperienceList
        workExperiences={workExperiences}
        onDelete={handleDeleteExperience}
      />
    </Box>
  );
};

export default WorkExperienceForm;